import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getPagePrompts } from "../utils/pagePrompts";

/**
 * SuggestionBubble – A pre-chat hint bubble that floats next to the Tara chatbot button.
 * Shows rotating suggestion messages when the chatbot is closed.
 *
 * Props:
 *  - onClick: callback when bubble is clicked (typically opens chatbot)
 */
export default function SuggestionBubble({ onClick }) {
  const location = useLocation();
  const [prompts, setPrompts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(false); // controls fade in/out
  const [shouldRender, setShouldRender] = useState(false); // controls DOM mount

  // Fetch prompts whenever the route changes
  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        const pagePrompts = await getPagePrompts(location.pathname);
        setPrompts(pagePrompts || []);
      } catch {
        setPrompts([]);
      }
    };

    fetchPrompts();
  }, [location.pathname]);

  // Rotation cycle: fade in → hold 3s → fade out → switch → repeat
  useEffect(() => {
    if (prompts.length === 0) return;

    // Initial delay before first appearance (1.5s after mount)
    const initialTimer = setTimeout(() => {
      setShouldRender(true);
      // small delay so the DOM is mounted before we trigger opacity transition
      requestAnimationFrame(() => setVisible(true));
    }, 1500);

    return () => clearTimeout(initialTimer);
  }, [prompts]);

  // Handle the rotation loop
  useEffect(() => {
    if (!shouldRender || prompts.length === 0) return;

    const cycleTimer = setInterval(() => {
      // Fade out
      setVisible(false);

      // Wait for fade-out, then switch prompt and fade in
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % prompts.length);
        setVisible(true);
      }, 500); // matches fade-out duration
    }, 5000); // Total cycle: 5s visible

    return () => clearInterval(cycleTimer);
  }, [shouldRender, prompts.length]);

  if (prompts.length === 0 || !shouldRender) return null;

  const currentPrompt = prompts[currentIndex];
  if (!currentPrompt) return null;

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        aria-label="Chat with Tara"
        title="Click to chat with Tara"
        style={{
          position: "absolute",
          top: "50%",
          right: "calc(100% + 12px)",
          zIndex: 1999,
          width: "max-content",
          maxWidth: "260px",
          padding: "12px 16px",
          background: "linear-gradient(145deg, #1e4a4a 0%, #2d5a5a 100%)",
          color: "#fff",
          borderRadius: "16px",
          border: "1px solid rgba(255, 255, 255, 0.15)",
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
          fontFamily: "Inter, sans-serif",
          fontSize: "13px",
          lineHeight: "1.5",
          cursor: "pointer",
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(-50%) translateX(0)" : "translateY(-50%) translateX(10px)",
          transition: "opacity 0.4s ease, transform 0.4s ease",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          textAlign: "left",
        }}
      >
        {/* Arrow pointing right toward the chatbot button */}
        <div
          style={{
            position: "absolute",
            right: "-6px",
            top: "50%",
            transform: "translateY(-50%)",
            width: 0,
            height: 0,
            borderTop: "6px solid transparent",
            borderBottom: "6px solid transparent",
            borderLeft: "6px solid #1e4a4a",
          }}
        />
        <img
          src="/images/fluent_prompt-icon.svg"
          alt=""
          width={14}
          height={14}
          style={{ opacity: 0.7, flexShrink: 0 }}
        />
        <span style={{ flex: 1 }}>{currentPrompt.text}</span>
      </button>
    </>
  );
}
